import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useConsultation } from '../context/ConsultationContext';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getImageUrl } from '../config';
import { settingsService } from '../services/settingsService';

const ConsultationContainer = styled.div`
  min-height: calc(100vh - 200px);
  padding: 2rem 0;
`;

const ConsultationContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const ConsultationTitle = styled.h1`
  color: var(--primary-blue);
  font-size: 1.5rem;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const EmptyConsultation = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  
  h2 {
    color: var(--gray-medium);
    margin-bottom: 1rem;
  }
  
  p {
    color: var(--gray-medium);
    margin-bottom: 2rem;
  }
`;

const ContinueShoppingButton = styled.button`
  padding: 12px 24px;
  background: var(--primary-blue);
  color: white;
  border: none;
  border-radius: var(--border-radius);
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition);
  
  &:hover {
    background: #081050;
  }
`;

const ConsultationGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ConsultationItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ConsultationItem = styled.div`
  background: white;
  border-radius: var(--border-radius);
  box-shadow: var(--shadow);
  padding: 1.5rem;
  display: grid;
  grid-template-columns: 100px 1fr auto auto auto;
  gap: 1.25rem;
  align-items: center;
  
  @media (max-width: 768px) {
    grid-template-columns: 80px 1fr;
    gap: 1rem;
  }
`;

const ItemImage = styled.img`
  width: 100px;
  height: 100px;
  object-fit: contain;
  border-radius: var(--border-radius);
  
  @media (max-width: 768px) {
    width: 80px;
    height: 80px;
  }
`;

const ItemInfo = styled.div`
  h3 {
    color: var(--gray-dark);
    margin-bottom: 0.35rem;
    font-size: 1.05rem;
    font-weight: 600;
  }
  
  p {
    color: var(--gray-medium);
    font-size: 0.85rem;
    margin-bottom: 0.5rem;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  
  .category {
    background: var(--gray-light);
    color: var(--gray-dark);
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-size: 0.75rem;
    display: inline-block;
    margin-bottom: 0.35rem;
  }

  .unit-price {
    color: #4b5563;
    font-size: 0.9rem;
    font-weight: 500;
    margin-top: 0.25rem;
  }
`;

const PriceColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  min-width: 90px;

  .subtotal-label {
    font-size: 0.75rem;
    color: #6b7280;
    text-transform: uppercase;
  }

  .subtotal-amount {
    font-size: 1.1rem;
    font-weight: 700;
    color: var(--primary-blue, #1e3a8a);
  }

  @media (max-width: 768px) {
    grid-column: 1 / -1;
    align-items: center;
    margin-top: 0.5rem;
  }
`;

const QuantityControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    grid-column: 1 / -1;
    justify-content: center;
    margin-top: 0.5rem;
  }
`;

const QuantityButton = styled.button`
  width: 32px;
  height: 32px;
  border: 1px solid var(--gray-medium);
  background: white;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: var(--transition);
  
  &:hover {
    background: var(--primary-blue);
    color: white;
    border-color: var(--primary-blue);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const QuantityDisplay = styled.span`
  min-width: 36px;
  text-align: center;
  font-weight: 600;
  font-size: 1rem;
`;

const RemoveButton = styled.button`
  background: #fee2e2;
  color: #dc2626;
  border: 1px solid #fecaca;
  border-radius: 6px;
  padding: 8px 10px;
  cursor: pointer;
  transition: var(--transition);
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: #dc2626;
    color: white;
  }
  
  @media (max-width: 768px) {
    grid-column: 1 / -1;
    margin-top: 0.25rem;
  }
`;

const ConsultationSummary = styled.div`
  background: white;
  border-radius: var(--border-radius);
  box-shadow: var(--shadow);
  padding: 2rem;
  height: fit-content;
  position: sticky;
  top: 100px;
`;

const SummaryTitle = styled.h3`
  color: var(--primary-blue);
  margin-bottom: 1.5rem;
  font-size: 1.3rem;
  font-weight: 700;
  border-bottom: 2px solid var(--gray-light, #f3f4f6);
  padding-bottom: 0.75rem;
`;

const SummaryItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.9rem;
  font-size: 0.95rem;
  color: #374151;

  .item-name {
    flex: 1;
    margin-right: 0.75rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .item-subtotal {
    font-weight: 600;
    color: #1f2937;
    white-space: nowrap;
  }
  
  &.divider {
    border-top: 1px solid #e5e7eb;
    margin-top: 1rem;
    padding-top: 0.75rem;
  }

  &.total {
    font-weight: 700;
    font-size: 1.35rem;
    color: #166534;
    border-top: 2px solid #e5e7eb;
    padding-top: 1.25rem;
    margin-top: 1.25rem;
    
    .total-amount {
      color: #166534;
    }
  }
`;

const WhatsAppButton = styled.button`
  width: 100%;
  padding: 16px;
  background: #25D366;
  color: white;
  border: none;
  border-radius: var(--border-radius);
  font-size: 1.1rem;
  font-weight: 700;
  cursor: pointer;
  transition: var(--transition);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  margin-top: 2rem;
  box-shadow: 0 4px 14px rgba(37, 211, 102, 0.35);
  
  &:hover {
    background: #1da851;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(37, 211, 102, 0.45);
  }
`;

const Consultation: React.FC = () => {
  const { consultation, updateQuantity, removeFromConsultation, itemCount, clearConsultation } = useConsultation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [whatsappNumber, setWhatsappNumber] = useState('51999999999');

  const handleQuantityChange = (productId: string, newQuantity: number) => {
    if (newQuantity <= 0) {
      removeFromConsultation(productId);
    } else {
      updateQuantity(productId, newQuantity);
    }
  };

  const handleRemoveItem = (productId: string, productName: string) => {
    removeFromConsultation(productId);
  };

  // Cargar número de WhatsApp al montar el componente
  useEffect(() => {
    const loadWhatsappNumber = async () => {
      try {
        const settings = await settingsService.getSettings();
        if (settings.whatsapp_number) {
          setWhatsappNumber(settings.whatsapp_number.replace(/[^0-9]/g, '') || '51999999999');
        }
      } catch (error) {
        // Fallback al número por defecto
      }
    };
    loadWhatsappNumber();
  }, []);

  // Función para obtener el precio activo (evalúa precio de oferta si está activo)
  const getActivePrice = (product: any): number => {
    if (product?.use_offer_price && Number(product?.offer_price || 0) > 0) {
      return Number(product.offer_price);
    }
    return Number(product?.price || 0);
  };

  // Función para checkout directo hacia WhatsApp con boleta digital
  const handleWhatsAppCheckout = () => {
    if (!user) {
      toast.error('Debes iniciar sesión para realizar una compra');
      navigate('/login');
      return;
    }

    if (consultation.items.length === 0) {
      toast.warning('Tu carrito está vacío');
      return;
    }

    // 1. Calcular el total a pagar sumando precio * cantidad
    const totalPagar = consultation.items.reduce(
      (sum, item) => sum + getActivePrice(item.product) * item.quantity,
      0
    );

    // 2. Construir lista de items formateados
    const itemsTexto = consultation.items
      .map((item) => {
        const unitPrice = getActivePrice(item.product);
        const subtotal = (unitPrice * item.quantity).toFixed(2);
        return `▪️ ${item.quantity}x *${item.product.name}* - S/. ${subtotal}`;
      })
      .join('\n');

    // 3. Construir mensaje con Template Literals y estructura exacta
    const mensaje = `¡Hola equipo de Vargas Homecenter! 👋 Me gustaría realizar la compra de los siguientes productos:

🛍️ MI PEDIDO:
${itemsTexto}

💰 TOTAL A PAGAR: S/. ${totalPagar.toFixed(2)}

Quedo atento a las indicaciones para el pago y la entrega. ¡Gracias!`;

    // 4. Codificar mensaje con encodeURIComponent
    const mensajeCodificado = encodeURIComponent(mensaje);

    // 5. Abrir nueva pestaña hacia WhatsApp
    const targetPhone = whatsappNumber || '51999999999';
    window.open(`https://wa.me/${targetPhone}?text=${mensajeCodificado}`, '_blank');

    // 6. Limpiar carrito y notificar
    clearConsultation();
    toast.success('Pedido enviado a WhatsApp.');
  };

  // Calcular total a pagar global para el resumen
  const totalPagar = consultation.items.reduce(
    (sum, item) => sum + getActivePrice(item.product) * item.quantity,
    0
  );

  if (consultation.items.length === 0) {
    return (
      <ConsultationContainer>
        <ConsultationContent>
          <ConsultationTitle>
            Mi Carrito
          </ConsultationTitle>
          <EmptyConsultation>
            <h2>Tu Carrito está vacío</h2>
            <p>¡Agrega algunos productos para comenzar!</p>
            <ContinueShoppingButton onClick={() => navigate('/productos')}>
              Ver Productos
            </ContinueShoppingButton>
          </EmptyConsultation>
        </ConsultationContent>
      </ConsultationContainer>
    );
  }

  return (
    <ConsultationContainer>
      <ConsultationContent>
        <ConsultationTitle>
          Mi Carrito ({itemCount} {itemCount === 1 ? 'producto' : 'productos'})
        </ConsultationTitle>
        
        <ConsultationGrid>
          <ConsultationItems>
            {consultation.items.map((item) => {
              const itemPrice = getActivePrice(item.product);
              const itemSubtotal = itemPrice * item.quantity;
              const hasOffer = item.product.use_offer_price && Number(item.product.offer_price || 0) > 0;

              return (
                <ConsultationItem key={item.product.id}>
                  <ItemImage 
                    src={getImageUrl(item.product.image_url)}
                    alt={item.product.name}
                    onError={(e) => {
                      if (e && e.target) {
                        (e.target as HTMLImageElement).src = '/placeholder-image.jpg';
                      }
                    }}
                  />
                  
                  <ItemInfo>
                    <h3>{item.product.name}</h3>
                    <p>{item.product.description}</p>
                    <span className="category">{item.product.category_name || 'Sin categoría'}</span>
                    <div className="unit-price">
                      Precio unitario: {hasOffer ? (
                        <span style={{ display: "inline-flex", gap: "0.4rem", alignItems: "baseline" }}>
                          <del style={{ color: "#9ca3af", fontSize: "0.85rem" }}>
                            S/. {Number(item.product.price || 0).toFixed(2)}
                          </del>
                          <strong style={{ color: "#ea580c" }}>
                            S/. {itemPrice.toFixed(2)}
                          </strong>
                        </span>
                      ) : (
                        <strong>S/. {itemPrice.toFixed(2)}</strong>
                      )}
                    </div>
                  </ItemInfo>
                  
                  <QuantityControls>
                    <QuantityButton 
                      onClick={() => handleQuantityChange(item.product.id, item.quantity - 1)}
                      disabled={item.quantity <= 1}
                      title="Disminuir cantidad"
                    >
                      ➖
                    </QuantityButton>
                    <QuantityDisplay>{item.quantity}</QuantityDisplay>
                    <QuantityButton 
                      onClick={() => handleQuantityChange(item.product.id, item.quantity + 1)}
                      title="Aumentar cantidad"
                    >
                      ➕
                    </QuantityButton>
                  </QuantityControls>

                  <PriceColumn>
                    <span className="subtotal-label">Subtotal</span>
                    <span className="subtotal-amount">S/. {itemSubtotal.toFixed(2)}</span>
                  </PriceColumn>
                  
                  <RemoveButton 
                    onClick={() => handleRemoveItem(item.product.id, item.product.name)}
                    title="Eliminar producto"
                  >
                    🗑️
                  </RemoveButton>
                </ConsultationItem>
              );
            })}
          </ConsultationItems>
          
          <ConsultationSummary>
            <SummaryTitle>Resumen de Compra</SummaryTitle>
            
            {consultation.items.map((item) => {
              const itemSubtotal = getActivePrice(item.product) * item.quantity;
              return (
                <SummaryItem key={item.product.id}>
                  <span className="item-name" title={item.product.name}>
                    {item.quantity}x {item.product.name}
                  </span>
                  <span className="item-subtotal">S/. {itemSubtotal.toFixed(2)}</span>
                </SummaryItem>
              );
            })}
            
            <SummaryItem className="divider">
              <span>Total de unidades:</span>
              <strong>{itemCount}</strong>
            </SummaryItem>
            
            <SummaryItem className="total">
              <span>Total a Pagar:</span>
              <span className="total-amount">S/. {totalPagar.toFixed(2)}</span>
            </SummaryItem>
            
            <WhatsAppButton onClick={handleWhatsAppCheckout}>
              <span>💬</span> Comprar por WhatsApp
            </WhatsAppButton>
            
            <ContinueShoppingButton 
              onClick={() => navigate('/productos')}
              style={{ width: '100%', marginTop: '1rem' }}
            >
              Seguir Comprando
            </ContinueShoppingButton>
          </ConsultationSummary>
        </ConsultationGrid>
      </ConsultationContent>
    </ConsultationContainer>
  );
};

export default Consultation;